import { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Building2, Plus, LogOut, ExternalLink, Users, DollarSign, Calendar, Mail, X, ShieldCheck, ArrowRight, Inbox, Ban, Trash2, Pause, Play, Activity, UserCheck, Download, Star } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Wordmark } from "@/components/Logo";

function StatChip({ label, value, mono = false }) {
  return (
    <div className="flex flex-col">
      <span className="label-tiny" style={{ color: "var(--text-dim)" }}>{label}</span>
      <span className={`font-bold text-[15px] ${mono ? "mono" : ""}`}>{value}</span>
    </div>
  );
}

function PlanPill({ plan }) {
  const color = plan === "federation" ? "var(--green)" : plan === "academy" ? "#fff" : "var(--text-dim)";
  return <span className="pill capitalize" style={{ borderColor: color, color }}>{plan}</span>;
}

function StatusPill({ status }) {
  const map = {
    active:    { bg: "rgba(61,220,126,0.15)", fg: "var(--green)" },
    trial:     { bg: "rgba(255,220,80,0.15)", fg: "#ffd452" },
    suspended: { bg: "rgba(255,80,80,0.15)",  fg: "var(--danger)" },
  };
  const s = map[status] || map.active;
  return <span className="pill" style={{ background: s.bg, color: s.fg, borderColor: s.fg }}>{status}</span>;
}

function CreateTenantModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", owner_email: "", academy_id: "", timezone: "America/Panama",
    plan: "tatame", country: "PA",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const body = { ...form };
      if (!body.academy_id?.trim()) delete body.academy_id;
      const { data } = await api.post("/admin/tenants", body);
      onCreated(data.tenant);
    } catch (err) {
      setError(err?.response?.data?.detail || "Create failed");
    }
    setSaving(false);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"grid",placeItems:"center",zIndex:60,padding:16}} onClick={onClose}>
      <form onSubmit={submit} onClick={e=>e.stopPropagation()} className="card p-6 w-[480px] max-w-[92vw]" data-testid="tenant-create-modal">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="label" style={{color:"var(--green)"}}>Super admin</div>
            <div className="text-[20px] font-bold uppercase mt-1 tracking-tight">Add academy</div>
          </div>
          <button type="button" onClick={onClose} data-testid="tenant-create-close"><X size={18}/></button>
        </div>
        <div className="space-y-3">
          <label className="block label">Academy name
            <input className="input mt-1" required placeholder="Gracie Barra Panama" value={form.name} onChange={set("name")} data-testid="tenant-name"/>
          </label>
          <label className="block label">Owner email
            <input className="input mt-1" required type="email" placeholder="owner@academy.com" value={form.owner_email} onChange={set("owner_email")} data-testid="tenant-owner-email"/>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block label">Academy slug <span className="label-tiny normal-case" style={{color:"var(--text-dim)"}}>(optional)</span>
              <input className="input mt-1" placeholder="auto from name" value={form.academy_id} onChange={set("academy_id")} data-testid="tenant-slug"/>
            </label>
            <label className="block label">Plan
              <select className="input mt-1" value={form.plan} onChange={set("plan")} data-testid="tenant-plan">
                <option value="tatame">Tatame ($59)</option>
                <option value="academy">Academy ($119)</option>
                <option value="federation">Federation ($299)</option>
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block label">Timezone
              <input className="input mt-1" value={form.timezone} onChange={set("timezone")} data-testid="tenant-tz"/>
            </label>
            <label className="block label">Country
              <input className="input mt-1" value={form.country} onChange={set("country")} data-testid="tenant-country"/>
            </label>
          </div>
        </div>
        {error && <div className="label-tiny mt-3" style={{color:"var(--danger)"}} data-testid="tenant-create-error">{error}</div>}
        <button type="submit" disabled={saving} className="btn btn-primary w-full mt-5" data-testid="tenant-create-submit">
          {saving ? "Creating…" : "Create academy & invite owner"}
        </button>
      </form>
    </div>
  );
}

function DeleteTenantModal({ tenant, onClose, onDeleted }) {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const matches = confirmText.trim() === tenant.academy_id;

  const doDelete = async () => {
    setBusy(true); setError("");
    try {
      await api.delete(`/admin/tenants/${tenant.academy_id}`);
      onDeleted(tenant.academy_id);
    } catch (err) {
      setError(err?.response?.data?.detail || "Delete failed");
      setBusy(false);
    }
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"grid",placeItems:"center",zIndex:60,padding:16}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="card p-6 w-[440px] max-w-[92vw]" style={{borderColor:"rgba(255,80,80,0.35)"}} data-testid="tenant-delete-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="label" style={{color:"rgb(255,120,120)"}}>Danger zone</div>
          <button onClick={onClose} data-testid="tenant-delete-close"><X size={18}/></button>
        </div>
        <div className="text-[18px] font-bold uppercase tracking-tight">Delete {tenant.name}</div>
        <div className="label-tiny mt-2" style={{color:"var(--text-dim)"}}>
          This permanently wipes all students, classes, attendance, announcements, and payment history for this academy. This cannot be undone.
        </div>
        <label className="block label mt-4">
          Type <span className="mono" style={{color:"rgb(255,120,120)"}}>{tenant.academy_id}</span> to confirm
          <input className="input mt-1" value={confirmText} onChange={e=>setConfirmText(e.target.value)} data-testid="tenant-delete-confirm-input" placeholder={tenant.academy_id}/>
        </label>
        {error && <div className="label-tiny mt-3" style={{color:"var(--danger)"}} data-testid="tenant-delete-error">{error}</div>}
        <button
          onClick={doDelete}
          disabled={!matches || busy}
          className="btn btn-primary w-full mt-5"
          style={{ background: matches ? "rgb(220,60,60)" : undefined, color: matches ? "white" : undefined }}
          data-testid="tenant-delete-confirm-submit"
        >
          <Trash2 size={14}/> {busy ? "Deleting…" : "Delete permanently"}
        </button>
      </div>
    </div>
  );
}

function PresetsModal({ tenant, onClose, onSaved }) {
  const [presets, setPresets] = useState(() => (tenant.plan_presets || []).map(p => ({ name: p.name, price: p.price })));
  const [feeLabel, setFeeLabel] = useState(tenant.annual_fee?.label || "");
  const [feeAmount, setFeeAmount] = useState(tenant.annual_fee?.amount || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const updateRow = (i, key, val) => setPresets(presets.map((p, idx) => idx === i ? { ...p, [key]: val } : p));
  const addRow = () => setPresets([...presets, { name: "", price: "" }]);
  const removeRow = (i) => setPresets(presets.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true); setError("");
    try {
      const cleanPresets = presets
        .filter(p => p.name.trim())
        .map(p => ({ name: p.name.trim(), price: Number(p.price) || 0 }));
      const annual_fee = feeLabel.trim() && feeAmount ? { label: feeLabel.trim(), amount: Number(feeAmount) } : null;
      await api.patch(`/admin/tenants/${tenant.academy_id}`, { plan_presets: cleanPresets, annual_fee });
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"grid",placeItems:"center",zIndex:60,padding:16}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="card p-6 w-[560px] max-w-[94vw] max-h-[88vh] overflow-y-auto" data-testid="tenant-presets-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label" style={{color:"var(--green)"}}>Pricing</div>
            <div className="text-[18px] font-bold uppercase tracking-tight">{tenant.name} — membership presets</div>
          </div>
          <button onClick={onClose} data-testid="tenant-presets-close"><X size={18}/></button>
        </div>
        <div className="label-tiny mb-3" style={{color:"var(--text-dim)"}}>
          Shown as a dropdown to this academy's owner (Students page) and to new students signing up at /join/{tenant.slug}. Leave empty to use the default free-text plan field.
        </div>
        <div className="space-y-2">
          {presets.map((p, i) => (
            <div key={i} className="grid gap-2 items-center" style={{gridTemplateColumns:"1fr 110px 28px"}} data-testid={`preset-row-${i}`}>
              <input className="input" placeholder="Discipline name" value={p.name} onChange={e=>updateRow(i,"name",e.target.value)} data-testid={`preset-name-${i}`}/>
              <input className="input" style={{textAlign:"right"}} type="number" min="0" placeholder="$/mo" value={p.price} onChange={e=>updateRow(i,"price",e.target.value)} data-testid={`preset-price-${i}`}/>
              <button type="button" onClick={()=>removeRow(i)} data-testid={`preset-remove-${i}`}><Trash2 size={14} style={{color:"rgb(255,120,120)"}}/></button>
            </div>
          ))}
          <button type="button" onClick={addRow} className="btn btn-outline w-full" data-testid="preset-add-row">
            <Plus size={13}/> Add discipline
          </button>
        </div>
        <div className="label mt-5" style={{color:"var(--green)"}}>One-time annual fee (optional)</div>
        <div className="label-tiny mb-2" style={{color:"var(--text-dim)"}}>e.g. Matricula Anual — charged once, never rolls the monthly renewal date.</div>
        <div className="grid gap-2" style={{gridTemplateColumns:"1fr 110px"}}>
          <input className="input" placeholder="Label (e.g. Matricula Anual)" value={feeLabel} onChange={e=>setFeeLabel(e.target.value)} data-testid="annual-fee-label"/>
          <input className="input" style={{textAlign:"right"}} type="number" min="0" placeholder="$" value={feeAmount} onChange={e=>setFeeAmount(e.target.value)} data-testid="annual-fee-amount"/>
        </div>
        {error && <div className="label-tiny mt-3" style={{color:"var(--danger)"}} data-testid="tenant-presets-error">{error}</div>}
        <button onClick={save} disabled={busy} className="btn btn-primary w-full mt-5" data-testid="tenant-presets-save">
          {busy ? "Saving…" : "Save pricing"}
        </button>
      </div>
    </div>
  );
}

function TenantRow({ t, onImpersonate, onToggleSuspend, onToggleComped, onDeleteRequest, onEditPresets }) {
  const isFlagship = t.comped;
  return (
    <div className="card p-5" data-testid={`tenant-row-${t.academy_id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
        <div className="min-w-0 w-full sm:w-auto">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-[16px] uppercase tracking-tight break-words">{t.name}</span>
            <StatusPill status={t.status} />
            <PlanPill plan={t.plan} />
          </div>
          <div className="label-tiny mt-1 mono break-all" style={{color:"var(--text-dim)"}}>{t.academy_id} · {t.owner_email || "no owner"}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <button
            onClick={() => onImpersonate(t)}
            className="btn btn-outline"
            data-testid={`tenant-impersonate-${t.academy_id}`}
            title="View as owner of this academy"
          >
            <ExternalLink size={13}/> Open
          </button>
          <button
            onClick={() => onToggleComped(t)}
            className="btn btn-outline"
            style={isFlagship ? { color: "var(--green)", borderColor: "var(--green-border)" } : undefined}
            title={isFlagship ? "Remove comped/flagship status (re-enable billing)" : "Mark as comped/flagship (bypass all billing)"}
            data-testid={`tenant-comp-${t.academy_id}`}
          >
            <Star size={13}/> {isFlagship ? "Comped" : "Comp"}
          </button>
          <button
            onClick={() => onEditPresets(t)}
            className="btn btn-outline"
            title="Edit this academy's membership plan presets + annual fee"
            data-testid={`tenant-presets-${t.academy_id}`}
          >
            <DollarSign size={13}/> Pricing
          </button>
          {!isFlagship && (
            <>
              <button
                onClick={() => onToggleSuspend(t)}
                className="btn btn-outline"
                title={t.status === "suspended" ? "Reactivate academy" : "Suspend academy (reversible)"}
                data-testid={`tenant-suspend-${t.academy_id}`}
              >
                {t.status === "suspended" ? <Play size={13}/> : <Pause size={13}/>}
              </button>
              <button
                onClick={() => onDeleteRequest(t)}
                className="btn btn-outline"
                style={{ color: "rgb(255,120,120)", borderColor: "rgba(255,80,80,0.35)" }}
                title="Delete permanently"
                data-testid={`tenant-delete-${t.academy_id}`}
              >
                <Trash2 size={13}/>
              </button>
            </>
          )}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3 pt-3" style={{borderTop:"1px solid var(--border)"}}>
        <StatChip label="Students" value={<><Users size={11} style={{display:"inline",marginRight:4,color:"var(--text-dim)"}}/>{t.students_count}</>} />
        <StatChip label="Active" value={t.active_students} />
        <StatChip label="Classes" value={<><Calendar size={11} style={{display:"inline",marginRight:4,color:"var(--text-dim)"}}/>{t.classes_count}</>} />
        <StatChip label="MRR" value={<><DollarSign size={11} style={{display:"inline",marginRight:4,color:"var(--green)"}}/>${t.mrr}</>} mono />
      </div>
    </div>
  );
}

function LeadsPanel({ leads, onApproved, onRejected }) {
  const [approving, setApproving] = useState(null);
  const [form, setForm] = useState({ academy_name: "", academy_id: "" });

  const startApprove = (lead) => {
    setApproving(lead);
    setForm({ academy_name: lead.academy_name || "", academy_id: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/leads/approve", {
        lead_id: approving.lead_id,
        academy_name: form.academy_name,
        academy_id: form.academy_id || undefined,
      });
      setApproving(null);
      onApproved();
    } catch (err) {
      alert(err?.response?.data?.detail || "Approve failed");
    }
  };

  const reject = async (lead) => {
    if (!window.confirm(`Reject ${lead.email}?`)) return;
    try {
      await api.delete(`/admin/leads/${lead.lead_id}`);
      onRejected();
    } catch (err) {
      alert(err?.response?.data?.detail || "Reject failed");
    }
  };

  const pending = leads.filter(l => !l.approved_at && !l.rejected_at);

  return (
    <>
      <div className="card p-5" data-testid="leads-panel">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Inbox size={15} style={{ color: "var(--green)" }} />
            <div className="label" style={{color:"var(--text)"}}>Prospects · {pending.length} pending</div>
          </div>
        </div>
        {pending.length === 0 ? (
          <div className="label-tiny text-center py-6" style={{color:"var(--text-dim)"}}>No pending prospects.</div>
        ) : (
          <div className="space-y-2">
            {pending.map(l => (
              <div key={l.lead_id} className="flex items-center justify-between gap-3 p-3 rounded" style={{background:"var(--bg-elev)"}} data-testid={`lead-row-${l.lead_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[13px] truncate">{l.academy_name || "(no academy name)"}</div>
                  <div className="label-tiny truncate mono" style={{color:"var(--text-dim)"}}>
                    {l.name ? `${l.name} · ` : ""}{l.email} · {l.source}
                  </div>
                  <div className="label-tiny truncate" style={{color:"var(--text-dim)"}}>
                    {l.location ? `${l.location} · ` : ""}{l.approx_members ? `~${l.approx_members} members · ` : ""}{timeAgo(l.created_at)}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button className="btn btn-primary" onClick={()=>startApprove(l)} data-testid={`lead-approve-${l.lead_id}`}><ArrowRight size={12}/> Approve</button>
                  <button className="btn btn-outline" style={{color:"var(--danger)"}} onClick={()=>reject(l)} data-testid={`lead-reject-${l.lead_id}`}><Ban size={12}/></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {approving && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"grid",placeItems:"center",zIndex:60,padding:16}} onClick={()=>setApproving(null)}>
          <form onSubmit={submit} onClick={e=>e.stopPropagation()} className="card p-6 w-[420px] max-w-[92vw]" data-testid="lead-approve-modal">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="label" style={{color:"var(--green)"}}>Approve lead</div>
                <div className="text-[18px] font-bold uppercase mt-1 tracking-tight truncate">{approving.email}</div>
              </div>
              <button type="button" onClick={()=>setApproving(null)} data-testid="lead-approve-close"><X size={18}/></button>
            </div>
            <div className="space-y-3">
              <label className="block label">Academy name
                <input className="input mt-1" required value={form.academy_name} onChange={e=>setForm({...form,academy_name:e.target.value})} data-testid="lead-academy-name"/>
              </label>
              <label className="block label">Academy slug <span className="label-tiny normal-case" style={{color:"var(--text-dim)"}}>(optional)</span>
                <input className="input mt-1" placeholder="auto from name" value={form.academy_id} onChange={e=>setForm({...form,academy_id:e.target.value})} data-testid="lead-academy-slug"/>
              </label>
            </div>
            <button type="submit" className="btn btn-primary w-full mt-5" data-testid="lead-approve-submit">Create academy & send invite</button>
          </form>
        </div>
      )}
    </>
  );
}

function timeAgo(iso) {
  if (!iso) return "Never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "Never";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function csvDownload(rows, fields, filename) {
  const header = fields.join(",");
  const body = rows.map(r => fields.map(f => `"${String(r[f] ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function PlatformActivity({ tenants = [] }) {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [logQuery, setLogQuery] = useState("");
  const [logAcademy, setLogAcademy] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [emailing, setEmailing] = useState(null);

  const load = useCallback((since, until) => {
    const params = {};
    if (since) params.since = since;
    if (until) params.until = until;
    api.get("/admin/platform-stats").then(r => setStats(r.data)).catch(() => {});
    api.get("/admin/users", { params }).then(r => setUsers(r.data.rows || [])).catch(() => {});
    api.get("/admin/login-events", { params: { ...params, limit: 200 } }).then(r => setEvents(r.data.rows || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const applyRange = () => load(dateFrom || undefined, dateTo || undefined);
  const lastWeek = () => {
    const to = new Date().toISOString().slice(0, 10);
    const from = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
    setDateFrom(from); setDateTo(to);
    load(from, to);
  };
  const clearRange = () => { setDateFrom(""); setDateTo(""); load(); };

  const emailReport = async (report) => {
    setEmailing(report);
    try {
      const { data } = await api.post("/admin/reports/email", {
        report, since: dateFrom || undefined, until: dateTo || undefined,
      });
      alert(`Sent — ${data.rows} rows emailed to your inbox.`);
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not send email");
    }
    setEmailing(null);
  };

  // Merge in every known tenant (not just ones with login events yet) so a
  // brand-new academy with zero logins still shows up in the filter.
  const academyOptions = [...new Map([
    ...tenants.map(t => [t.academy_id, t.name]),
    ...events.map(e => [e.academy_id, e.academy_name]),
  ]).entries()];
  const q = logQuery.trim().toLowerCase();
  const filteredEvents = events.filter(e => {
    if (logAcademy && e.academy_id !== logAcademy) return false;
    if (!q) return true;
    return (e.email || "").toLowerCase().includes(q) || (e.name || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4" data-testid="platform-activity">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <div className="card p-4" data-testid="stat-total-users">
          <div className="label-tiny mb-1">Total users</div>
          <div className="text-[20px] font-bold">{stats ? stats.total_users : "…"}</div>
        </div>
        <div className="card p-4" data-testid="stat-total-students">
          <div className="label-tiny mb-1">Students</div>
          <div className="text-[20px] font-bold">{stats ? stats.total_students : "…"}</div>
        </div>
        <div className="card p-4" data-testid="stat-total-staff">
          <div className="label-tiny mb-1">Staff</div>
          <div className="text-[20px] font-bold">{stats ? stats.total_staff : "…"}</div>
        </div>
        <div className="card p-4" data-testid="stat-active-7d">
          <div className="label-tiny mb-1">Active (7d)</div>
          <div className="text-[20px] font-bold" style={{color:"var(--green)"}}>{stats ? stats.active_7d : "…"}</div>
        </div>
        <div className="card p-4" data-testid="stat-active-30d">
          <div className="label-tiny mb-1">Active (30d)</div>
          <div className="text-[20px] font-bold" style={{color:"var(--green)"}}>{stats ? stats.active_30d : "…"}</div>
        </div>
        <div className="card p-4" data-testid="stat-signups-30d">
          <div className="label-tiny mb-1">Signups (30d)</div>
          <div className="text-[20px] font-bold">{stats ? stats.signups_30d : "…"}</div>
        </div>
      </div>

      <div className="card p-4 flex items-center gap-2 flex-wrap">
        <span className="label-tiny" style={{color:"var(--text-dim)"}}>Date range (applies to both tables below):</span>
        <input type="date" className="input" style={{height: 30, fontSize: 12, width: 140}} value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="range-from" />
        <span className="label-tiny">to</span>
        <input type="date" className="input" style={{height: 30, fontSize: 12, width: 140}} value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="range-to" />
        <button onClick={applyRange} className="btn btn-outline" style={{padding:"5px 10px", fontSize: 11}} data-testid="range-apply">Apply</button>
        <button onClick={lastWeek} className="btn btn-outline" style={{padding:"5px 10px", fontSize: 11}} data-testid="range-last-week">Last 7 days</button>
        <button onClick={clearRange} className="btn btn-outline" style={{padding:"5px 10px", fontSize: 11}} data-testid="range-clear">Clear</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-5">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <UserCheck size={14} style={{color:"var(--green)"}}/>
              <div className="label">Platform users · by last active</div>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => csvDownload(users, ["name","email","role","academy_name","last_login_at","created_at"], "rolloss_users.csv")}
                      className="btn btn-outline" style={{padding:"5px 8px", fontSize: 11}} data-testid="users-download-csv">
                <Download size={11}/> CSV
              </button>
              <button onClick={() => emailReport("users")} disabled={emailing === "users"}
                      className="btn btn-outline" style={{padding:"5px 8px", fontSize: 11}} data-testid="users-email-csv">
                <Mail size={11}/> {emailing === "users" ? "Sending…" : "Email"}
              </button>
            </div>
          </div>
          <div style={{maxHeight: 320, overflowY: "auto"}}>
            {users.length === 0 && <div className="label-tiny py-4 text-center" style={{color:"var(--text-dim)"}} data-testid="users-empty">No users match this range.</div>}
            {users.map(u => (
              <div key={u.user_id} className="flex items-center justify-between gap-3 py-2 border-t" style={{borderColor:"var(--border)"}} data-testid={`user-row-${u.user_id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-[13px] truncate">{u.name || u.email}</div>
                  <div className="label-tiny truncate" style={{color:"var(--text-dim)"}}>{u.email} · {u.role} · {u.academy_name}</div>
                </div>
                <div className="label-tiny shrink-0 mono">{timeAgo(u.last_login_at)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Activity size={14} style={{color:"var(--green)"}}/>
              <div className="label">Recent logins · audit log</div>
            </div>
            <div className="flex items-center gap-1.5">
              <input
                className="input"
                style={{height: 30, fontSize: 12, width: 140}}
                placeholder="Search email/name"
                value={logQuery}
                onChange={e => setLogQuery(e.target.value)}
                data-testid="login-log-search"
              />
              <select
                className="input"
                style={{height: 30, fontSize: 12, width: 120}}
                value={logAcademy}
                onChange={e => setLogAcademy(e.target.value)}
                data-testid="login-log-academy-filter"
              >
                <option value="">All academies</option>
                {academyOptions.map(([id, name]) => <option key={id} value={id}>{name || id}</option>)}
              </select>
              <button onClick={() => csvDownload(filteredEvents, ["name","email","role","academy_name","login_at","ip"], "rolloss_login_events.csv")}
                      className="btn btn-outline" style={{padding:"5px 8px", fontSize: 11}} data-testid="events-download-csv">
                <Download size={11}/>
              </button>
              <button onClick={() => emailReport("login_events")} disabled={emailing === "login_events"}
                      className="btn btn-outline" style={{padding:"5px 8px", fontSize: 11}} data-testid="events-email-csv">
                <Mail size={11}/>
              </button>
            </div>
          </div>
          <div style={{maxHeight: 320, overflowY: "auto"}}>
            {filteredEvents.length === 0 && (
              <div className="label-tiny py-4 text-center" style={{color:"var(--text-dim)"}} data-testid="login-log-empty">
                {events.length === 0 ? "No login activity in this range." : "No logins match this filter."}
              </div>
            )}
            {filteredEvents.map(e => (
              <div key={e.event_id} className="flex items-center justify-between gap-3 py-2 border-t" style={{borderColor:"var(--border)"}} data-testid={`login-event-${e.event_id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-[13px] truncate">{e.name || e.email}</div>

                  <div className="label-tiny truncate" style={{color:"var(--text-dim)"}}>{e.role} · {e.academy_name} {e.ip ? `· ${e.ip}` : ""}</div>
                </div>
                <div className="label-tiny shrink-0 mono">{timeAgo(e.login_at)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminConsole() {
  const { user, loading, logout, checkAuth } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [leads, setLeads] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [presetsTarget, setPresetsTarget] = useState(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    const [tR, lR] = await Promise.all([
      api.get("/admin/tenants"),
      api.get("/admin/all-leads").catch(() => ({ data: [] })),
    ]);
    setTenants(tR.data.tenants || []);
    setLeads(lR.data || []);
  }, []);

  useEffect(() => { if (user?.role === "super_admin") load(); }, [user, load]);

  if (loading) return <div style={{minHeight:"100vh",display:"grid",placeItems:"center",background:"var(--bg)"}}><div className="label">Loading…</div></div>;
  if (!user) return <Navigate to="/app/login" replace />;
  if (user.role !== "super_admin") return <Navigate to="/app" replace />;

  const impersonate = async (t) => {
    try {
      await api.post("/admin/impersonate", { academy_id: t.academy_id });
      // Refresh auth state so the ImpersonationBanner + Exit control render
      // on the /app shell. Navigating without a checkAuth() leaves the client
      // convinced the user is a plain super_admin without an impersonation flag.
      if (checkAuth) await checkAuth();
      nav("/app");
    } catch (err) {
      alert(err?.response?.data?.detail || "Impersonate failed");
    }
  };

  const toggleSuspend = async (t) => {
    const nextStatus = t.status === "suspended" ? "active" : "suspended";
    if (!window.confirm(`${nextStatus === "suspended" ? "Suspend" : "Reactivate"} ${t.name}?`)) return;
    try {
      await api.patch(`/admin/tenants/${t.academy_id}`, { status: nextStatus });
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Update failed");
    }
  };

  const toggleComped = async (t) => {
    const nextComped = !t.comped;
    if (!window.confirm(nextComped
      ? `Mark ${t.name} as comped/flagship? This bypasses all billing enforcement for them.`
      : `Remove comped status from ${t.name}? Normal trial/billing rules will apply again.`)) return;
    try {
      await api.patch(`/admin/tenants/${t.academy_id}`, { comped: nextComped });
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Update failed");
    }
  };

  const totalStudents = tenants.reduce((n, t) => n + (t.students_count || 0), 0);
  const totalMrr = tenants.reduce((n, t) => n + (t.mrr || 0), 0);

  return (
    <div style={{background:"var(--bg)",minHeight:"100vh"}} data-testid="admin-console">
      <header className="flex items-center justify-between px-8 py-5 border-b" style={{borderColor:"var(--border)"}}>
        <div className="flex items-center gap-4">
          <Wordmark size={16} />
          <span className="pill" style={{borderColor:"var(--green)",color:"var(--green)"}}><ShieldCheck size={11} style={{display:"inline",marginRight:4}}/>Super admin</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="label-tiny mono truncate max-w-[220px]" style={{color:"var(--text-dim)"}}>{user.email}</span>
          <button className="btn btn-outline" onClick={logout} data-testid="admin-logout"><LogOut size={13}/> Sign out</button>
        </div>
      </header>

      <div className="p-8 max-w-[1200px] mx-auto space-y-6">
        <div className="flex items-end justify-between">
          <div>
            <div className="label" style={{color:"var(--green)"}}>RollOSS platform</div>
            <h1 className="text-[36px] font-bold tracking-tight mt-1 uppercase">Academies</h1>
            <div className="label-tiny mt-2" style={{color:"var(--text-dim)"}}>
              {tenants.length} tenants · {totalStudents} students · ${totalMrr}/mo MRR
            </div>
          </div>
          <button className="btn btn-primary" onClick={()=>setShowCreate(true)} data-testid="btn-create-tenant">
            <Plus size={13}/> Add academy
          </button>
        </div>

        <LeadsPanel leads={leads} onApproved={load} onRejected={load} />

        <PlatformActivity tenants={tenants} />

        {tenants.length === 0 ? (
          <div className="card p-10 text-center" data-testid="tenants-empty">
            <Building2 size={32} style={{color:"var(--text-dim)",margin:"0 auto"}}/>
            <div className="label mt-4">No academies yet</div>
            <div className="label-tiny mt-1" style={{color:"var(--text-dim)"}}>Approve a lead above or create one manually.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tenants.map(t => <TenantRow key={t.academy_id} t={t} onImpersonate={impersonate} onToggleSuspend={toggleSuspend} onToggleComped={toggleComped} onDeleteRequest={setDeleteTarget} onEditPresets={setPresetsTarget}/>)}
          </div>
        )}
      </div>

      {showCreate && <CreateTenantModal onClose={()=>setShowCreate(false)} onCreated={()=>{setShowCreate(false); load();}}/>}
      {deleteTarget && (
        <DeleteTenantModal
          tenant={deleteTarget}
          onClose={()=>setDeleteTarget(null)}
          onDeleted={()=>{ setDeleteTarget(null); load(); }}
        />
      )}
      {presetsTarget && (
        <PresetsModal
          tenant={presetsTarget}
          onClose={()=>setPresetsTarget(null)}
          onSaved={()=>{ setPresetsTarget(null); load(); }}
        />
      )}
    </div>
  );
}
